import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import { apiClient } from "../services/api";

// Create Auth Context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on initial load
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          // Set default auth header
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          // Get user data
          const response = await apiClient.get("/auth/me");
          setCurrentUser(response.data);
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
      } finally {
        setLoading(false);
      }
    };

    checkLoggedIn();
  }, []);

  // Login user with detailed error reporting
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Login attempt for:", email);
      console.log("API base URL:", apiClient.defaults.baseURL);

      // Attempt login with direct fetch to avoid any axios configuration issues
      try {
        const loginResponse = await fetch("/api/auth/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        });

        console.log("Login response status:", loginResponse.status);

        if (!loginResponse.ok) {
          const errorText = await loginResponse.text();
          console.error("Login API error response:", errorText);
          throw new Error(
            `Login failed with status ${loginResponse.status}: ${errorText}`
          );
        }

        const { token, user } = await loginResponse.json();

        // Save token to localStorage
        localStorage.setItem("token", token);

        // Set auth header
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // Set user in state
        setCurrentUser(user);

        return true;
      } catch (fetchErr) {
        console.error("Login fetch error:", fetchErr);
        throw fetchErr;
      }
    } catch (err) {
      console.error("Login failed:", err);
      const errorMessage = err.message || "Login failed. Please try again.";
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Register user with enhanced error handling
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Registration attempt for:", userData.email);

      // Attempt registration with direct fetch
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      console.log("Register response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Registration API error:", errorText);
        throw new Error(
          `Registration failed with status ${response.status}: ${errorText}`
        );
      }

      // For registration, we'll redirect to login afterwards
      return true;
    } catch (err) {
      console.error("Registration failed:", err);
      const errorMessage =
        err.message || "Registration failed. Please try again.";
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setCurrentUser(null);
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.put("/auth/profile", userData);
      setCurrentUser(response.data);
      return true;
    } catch (err) {
      console.error("Profile update failed:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Profile update failed. Please try again.";
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Check if user has admin role
  const isAdmin = () => {
    return currentUser?.role === "admin";
  };

  // Check if user has manager role
  const isManager = () => {
    return currentUser?.role === "admin" || currentUser?.role === "manager";
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        error,
        login,
        register,
        logout,
        updateProfile,
        isAdmin,
        isManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
